<script setup lang="ts">
import {
  ref, computed, reactive, onMounted,
} from 'vue';
import { storeToRefs, StoreGeneric } from 'pinia';
import { DropDownList } from '@progress/kendo-vue-dropdowns';
import ConnxButton from '@/components/Atoms/ConnxButton.vue';
import ConnxIcon from '@/components/Atoms/ConnxIcon.vue';
import ConnxField from '@/components/Molecules/Field/ConnxField.vue';
import ConnxAlert from '@/components/Molecules/ConnxAlert.vue';
import useEmployeeManagementActions from '@/stores/Employee/employee-management-actions';
import useApplicationStore from '@/stores/application';
import ConnxProgressSteps from '@/components/Molecules/ConnxProgressSteps.vue';
import Steps from '@/interfaces/Steps';
import { InputType } from '@/interfaces/InputFields';
import { LinkedRecord } from '@/interfaces/CBW/EmployeeLinkedRecordsResponse';

const reasonForDelete = ref({ label: 'Created incorrectly', value: 'created-incorrectly' });
const confirmChangeEmpCode = ref('N');
const confirmPayrollAttached = ref('N');
const confirmRetainData = ref('N');
const confirmEmployeeDelete = ref('');
const errorMessage = ref('');
const completed = ref(false);

const radioOptionsReasonForDelete = ref([
  { label: 'Created incorrectly', value: 'created-incorrectly' },
  { label: 'Duplicate record', value: 'duplicate-record' },
  { label: 'Setup incorrectly', value: 'setup-incorrectly' },
  { label: 'Never started', value: 'never-started' },
  { label: 'Record is not required anymore', value: 'record-is-not-required-anymore' },
  { label: 'Other', value: 'other' },
]);

const radioOptionsConfirmChangeEmpCode = ref([
  { label: 'Yes', value: 'Y' },
  { label: 'No', value: 'N' },
]);

const radioOptionsConfirmPayrollAttached = ref([
  { label: 'Yes', value: 'Y' },
  { label: 'No', value: 'N' },
]);

const radioOptionsConfirmRetainData = ref([
  { label: 'Yes', value: 'Y' },
  { label: 'No', value: 'N' },
]);

const showReasonConfirmChangeEmpCode = computed(() => {
  switch (reasonForDelete.value.value) {
    case ('setup-incorrectly'):
      return true;
    default:
      return false;
  }
});

const showReasonConfirmPayrollAttached = computed(() => {
  switch (reasonForDelete.value.value) {
    case ('created-incorrectly'):
    case ('duplicate-record'):
    case ('setup-incorrectly'):
      return true;
    default:
      return false;
  }
});

const showReasonConfirmRetainData = computed(() => {
  switch (reasonForDelete.value.value) {
    case ('created-incorrectly'):
    case ('duplicate-record'):
    case ('setup-incorrectly'):
      return true;
    default:
      return false;
  }
});

interface Props {
  employeeCode: string;
}

const currentStep = ref(1);
const steps: Steps[] = reactive([
  {
    title: 'Diagnostic Questions',
  },
  {
    title: 'Confirm Data Deletion',
  },
  {
    title: '',
    icon: 'fas fa-check',
  },
]);

const loading = ref(false);
const applicationStore = useApplicationStore();
const employeeManagementActions = useEmployeeManagementActions();
const { employee } = storeToRefs<StoreGeneric>(employeeManagementActions);
const props = defineProps<Props>();
const emit = defineEmits(['close']);

onMounted(async () => {
  applicationStore.setLoading(true);
  loading.value = true;

  await employeeManagementActions.init(props.employeeCode);
  await employeeManagementActions.fetchEmployeeLinkedRecordsResponseDetails();

  applicationStore.setLoading(false);
  loading.value = false;
});

const payrollSystem = computed(() => {
  switch (employeeManagementActions.companyExternalDbSetting?.payrollSystem) {
    case ('M'):
      return 'Micropay';
    case ('3'):
      return 'HR3';
    default:
      return '';
  }
});

const formattedLinkedRecord = (linkedRecord: LinkedRecord) => {
  const messageMap = new Map([
    [
      'Department',
      `Employee is set as a manager for ${linkedRecord.count} department${linkedRecord.count > 1 ? 's' : ''} (${linkedRecord.values?.join(', ')})`
    ],
    [
      'WorkflowProcessingName_Current',
      `Employee is linked to ${linkedRecord.count} current workflow approval${linkedRecord.count > 1 ? 's' : ''}`
    ],
    [
      'WorkflowProcessingName_Future',
      `Employee is linked to ${linkedRecord.count} future workflow approval${linkedRecord.count > 1 ? 's' : ''}`
    ],
    [
      'DistributionListName',
      `Employee is in ${linkedRecord.count} distribution list${linkedRecord.count > 1 ? 's' : ''} (${linkedRecord.values?.join(', ')})`
    ],
    [
      'WorkflowSequenceName',
      `Employee is in ${linkedRecord.count} workflow sequence${linkedRecord.count > 1 ? 's' : ''} (${linkedRecord.values?.join(', ')})`
    ],
    [
      'EmployeeTask',
      `Employee has ${linkedRecord.count} task${linkedRecord.count > 1 ? 's' : ''} to do`
    ],
    [
      'CourseSession_Current',
      `Employee is a facilitator of ${linkedRecord.count} current class${linkedRecord.count > 1 ? 'es' : ''}`
    ],
    [
      'EmployeeAppraisalParticipant_Current',
      `Employee is a participant in ${linkedRecord.count} current performance appraisal${linkedRecord.count > 1 ? 's' : ''}`
    ],
    [
      'EmployeeAppraisalParticipant_Past',
      `Employee is a participant in ${linkedRecord.count} past performance appraisal${linkedRecord.count > 1 ? 's' : ''}`
    ],
    [
      'Recruiter_Current',
      `Employee is the primary recruiter for ${linkedRecord.count} current ${linkedRecord.count > 1 ? 'vacancies' : 'vacancy'}`
    ],
    [
      'Recruiter_Completed',
      `Employee is a recruiter for ${linkedRecord.count} completed ${linkedRecord.count > 1 ? 'vacancies' : 'vacancy'}`
    ],
    [
      'Incident',
      `Employee is responsible for ${linkedRecord.count} WHS incident${linkedRecord.count > 1 ? 's' : ''}`
    ],
    [
      'IncidentAction',
      `Employee is linked to ${linkedRecord.count} WHS incident action${linkedRecord.count > 1 ? 's' : ''}`
    ],
    [
      'IncidentPersonsInvolved',
      `Employee is a person involved in ${linkedRecord.count} WHS incident${linkedRecord.count > 1 ? 's' : ''}`
    ],
    [
      'OutstandingVariation',
      `Employee has ${linkedRecord.count} outstanding variation${linkedRecord.count > 1 ? 's' : ''}`
    ],
    [
      'LeaveApplication',
      `Employee has ${linkedRecord.count} outstanding leave application${linkedRecord.count > 1 ? 's' : ''}`
    ],
    [
      'IncidentChecklist',
      `Employee has ${linkedRecord.count} outstanding items on the WHS to-do list`
    ],
    [
      'IssuedItems',
      `Employee has ${linkedRecord.count} issued item${linkedRecord.count > 1 ? 's' : ''} to be returned`
    ],
    [
      'ReportUser', 'Employee is a report user'],
    [
      'EmployeeClass',
      `Employee is linked to ${linkedRecord.count} current course session${linkedRecord.count > 1 ? 's' : ''}`
    ],
    [
      'Task',
      `Employee is set up as the employee to complete tasks for task sets (${linkedRecord.values?.join(', ')})`
    ],
    [
      'TaskSet',
      `Employee is set up as the backup employee for task sets (${linkedRecord.values?.join(', ')})`
    ],
    [
      'Employee_EmpCode',
      'Employee is set as approver',
    ],
    [
      'Employee_WorkflowReminderEmpCode',
      `Employee is setup as the backup approver of ${linkedRecord.count} workflow${linkedRecord.count > 1 ? 's' : ''}`
    ],
    [
      'Employee_RedirectApprovalEmpCode',
      `Employee is a backup approver of ${linkedRecord.count} (${linkedRecord.values?.join(', ')})`
    ]
  ]);

  if (messageMap.has(linkedRecord.entity)) {
    return messageMap.get(linkedRecord.entity);
  }
  if (messageMap.has(linkedRecord.field)) {
    return messageMap.get(linkedRecord.field);
  }
  if (messageMap.has(`${linkedRecord.entity}_${linkedRecord.field}`)) {
    return messageMap.get(`${linkedRecord.entity}_${linkedRecord.field}`);
  }

  return '';
};

const submit = async () => {
  // we can use applicationStore.setLoading(true) which will use the connx loader divProcessing or we can ignore it.
  applicationStore.setLoading(true);
  loading.value = true;
  const response = await employeeManagementActions.delete();
  applicationStore.setLoading(false);
  loading.value = false;

  currentStep.value = 3;

  if (response?.error) {
    // Error
    steps[2].cssOverrides = 'tw-bg-connx-danger-shade-10';
    steps[2].icon = 'far fa-times';
    steps[2].title = 'Error Encountered!';
    steps[2].cssTextOverrides = 'tw-text-connx-danger-shade-15';

    // eslint-disable-next-line prefer-destructuring
    errorMessage.value = response.error;
  } else {
    // Success
    completed.value = true;
    steps[2].title = 'Success!';
    steps[2].cssTextOverrides = 'tw-text-connx-success';
    steps[2].cssOverrides = 'tw-bg-connx-success';
  }
};

const decrementStep = () => {
  if (currentStep.value > 0) {
    currentStep.value -= 1;
  }
};

const incrementStep = () => {
  if (currentStep.value === 2) {
    submit();
  } else if (currentStep.value > 0) {
    currentStep.value += 1;
  }
};

const nextButtonDisabled = computed(() => {
  if (currentStep.value === 2) {
    return !confirmEmployeeDelete.value;
  }
  return false;
});

const formattedLinkedRecords = computed(() => employeeManagementActions.employeeLinkedRecords?.linkedRecords.map((linkedRecord) => formattedLinkedRecord(linkedRecord)).filter((linkedRecord) => linkedRecord !== ''));

</script>

<template>
  <div class="tw-fixed tw-top-0 tw-right-0 tw-bottom-0 tw-left-0 tw-z-1100 tw-backdrop-opacity-10 ">
    <div class="tw-py-10 tw-w-full tw-flex tw-flex-col">
      <div class="
          tw-bg-connx-white
          tw-rounded-md
          tw-mx-auto
          tw-border
          tw-border-solid
          tw-flex-1
          tw-w-full
          tw-max-w-[760px]
          tw-shadow-sm
          tw-flex
        ">
        <div class="tw-grow tw-flex tw-flex-col tw-justify-between">
          <header
            class="tw-border-solid tw-border-b tw-border-connx-grey-tint-80 tw-p-8 tw-flex tw-items-center tw-justify-between"
          >
            <h1 class="tw-text-2xl tw-font-semibold tw-text-connx-secondary-header tw-mb-0">
              Delete Employee Record
            </h1>
            <button
              type="button"
              class="tw-border-none tw-text-connx-grey-tint-20 hover:tw-text-connx-grey-shade-20"
              @click="emit('close')"
            >
              <connx-icon
                name="fal fa-times"
                class="tw-w-3"
              />
            </button>
          </header>
          <main class="
              tw-mx-auto
              tw-w-full
              tw-max-w-5xl
              tw-h-full
              tw-flex
              tw-flex-col
              tw-pt-10
              tw-justify-start
            ">
            <connx-progress-steps
              :current-step="currentStep"
              :steps="steps"
              class="tw-pb-4 tw-shadow"
            />
            <div class="tw-h-[590px] tw-pt-10 tw-px-8 ScrollArea">
              <div
                class="tw-flex tw-flex-wrap tw-justify-center"
                v-if="
                  employee
                  && employeeManagementActions.employeeLinkedRecords && !loading"
              >
                <div
                  class="tw-w-full tw-mb-10 tw-grid-cols-2 tw-gap-x-10 tw-gap-y-4 tw-mx-auto"
                  :class="[currentStep !== 1 ? 'tw-inline-grid' : 'tw-grid']"
                >
                  <h6 class="tw-mb-0 tw-leading-normal tw-text-connx-grey-shade-20">
                    Selected Employee:
                  </h6>
                  <div>{{ employee?.givenName }} {{ employee?.surname }}</div>
                  <h6 class="tw-mb-0 tw-leading-normal tw-text-connx-grey-shade-20">
                    Employee Code:
                  </h6>
                  <div>{{ employee?.empCode }}</div>
                  <template v-if="currentStep === 1">
                    <h6 class="tw-mb-0 tw-leading-normal tw-text-connx-grey-shade-20 tw">
                      What is your reason for <br />deleting the employee record?
                    </h6>
                    <div>
                      <DropDownList
                        v-model="reasonForDelete"
                        class="tw-z-1200 tw-mb-4 tw-text-base"
                        :data-items="radioOptionsReasonForDelete"
                        text-field="label"
                        :clear-button="false"
                        value-field="value"
                        placeholder="Please Select"
                      ></DropDownList>
                      <connx-alert
                        type="info"
                        v-if="reasonForDelete.value === 'other'"
                      >
                        We recommend you contact ConnX Support to discuss requirements.
                      </connx-alert>
                    </div>


                    <template v-if="showReasonConfirmChangeEmpCode">
                      <h6 class="tw-mb-0 tw-leading-normal tw-text-connx-grey-shade-20">
                        Will changing the employee code suit your requirement?
                      </h6>
                      <div>
                        <connx-field
                          v-model="confirmChangeEmpCode"
                          :unpadded="true"
                          name="confirmChangeEmpCode"
                          :inline="true"
                          :type="InputType.Radio"
                          :options="radioOptionsConfirmChangeEmpCode"
                        ></connx-field>
                        <connx-alert
                          v-if="confirmChangeEmpCode === 'Y'"
                          type="warning"
                          class="tw-mt-4"
                        >
                          You may want to consider running the change employee code process instead.
                        </connx-alert>
                      </div>
                    </template>
                    <template v-if="showReasonConfirmPayrollAttached
                      && employeeManagementActions.company
                      && employeeManagementActions.companyExternalDbSetting
                      && (employeeManagementActions.companyExternalDbSetting?.payrollSystem === 'M'
                        || employeeManagementActions.companyExternalDbSetting?.payrollSystem === '3')">
                      <h6 class="tw-mb-0 tw-leading-normal tw-text-connx-grey-shade-20">
                        The employee is currently attached
                        to a {{ payrollSystem }} Payroll System
                        ({{ employeeManagementActions.company?.companyName }}) in ConnX.
                        Are you planning to delete
                        the same in the payroll system?
                      </h6>
                      <div>
                        <connx-field
                          v-model="confirmPayrollAttached"
                          :unpadded="true"
                          name="confirmPayrollAttached"
                          :type="InputType.Radio"
                          :inline="true"
                          :options="radioOptionsConfirmPayrollAttached"
                        ></connx-field>
                        <connx-alert
                          type="warning"
                          v-if="confirmPayrollAttached === 'N'"
                        >
                          Deletion may not be suitable given that performing an
                          import process can bring the employee record back.
                        </connx-alert>
                      </div>



                    </template>
                    <template v-if="showReasonConfirmRetainData">
                      <h6 class="tw-mb-0 tw-leading-normal tw-text-connx-grey-shade-20">
                        Is there any data attached to the record that you want to retain?
                      </h6>
                      <div>
                        <connx-field
                          v-model="confirmRetainData"
                          :unpadded="true"
                          name="confirmRetainData"
                          :type="InputType.Radio"
                          :inline="true"
                          :options="radioOptionsConfirmRetainData"
                        ></connx-field>
                        <connx-alert
                          v-if="confirmRetainData === 'Y'"
                          type="warning"
                        >
                          Deletion is not suitable as it will not keep any data.
                        </connx-alert>
                      </div>

                    </template>
                    <template v-if="employeeManagementActions.employeeLinkedRecords?.linkedRecords.length > 0">
                      <h6 class="tw-mb-0 tw-leading-normal tw-text-connx-grey-shade-20">
                        The following conditions have been found for your consideration.
                        Select Next if you still wish to continue with deletion process.
                      </h6>
                      <ul class="tw-ml-6 tw-mb-10 tw-list-disc">
                        <li
                          v-for="(linkedRecord, key) in formattedLinkedRecords"
                          :key="key"
                        >
                          {{ linkedRecord }}
                        </li>
                      </ul>
                    </template>
                  </template>
                </div>
                <div v-if="currentStep === 2">
                  <div class="tw-flex tw-flex-wrap tw-justify-center">
                    <connx-alert
                      type="warning"
                      class="tw-mb-4 tw-w-full"
                    >
                      <div class="tw-w-full">
                        <div class="tw-mb-2">
                          NOTE: If you continue with the process, records for the employee will be
                          deleted in all places where they exist. There is no way to roll them back
                          if you accidentally selected the wrong employee.
                        </div>
                        <div>
                          Also, in places where this employee is recorded as the person who
                          performed the work will be replaced by your details.
                        </div>
                      </div>
                    </connx-alert>
                  </div>
                </div>
                <div v-if="currentStep === 3">
                  <div
                    v-if="errorMessage"
                    class="tw-flex tw-justify-center"
                  >
                    <connx-alert type="error">
                      <div>
                        <h6 class="tw-mb-0 tw-leading-normal tw-text-connx-grey-shade-20">
                          The employee record of
                          {{ employee.givenName }} {{ employee.surname }} ({{ employee.empCode }}) was not deleted.
                        </h6>
                        Error: {{ errorMessage }}
                      </div>
                    </connx-alert>
                  </div>
                  <div
                    v-else
                    class="tw-flex tw-justify-center"
                  >
                    <connx-alert type="success">
                      All records of
                      {{ employee.givenName }} {{ employee.surname }} ({{ employee.empCode }})
                      have been deleted successfully.
                    </connx-alert>
                  </div>
                </div>
              </div>
            </div>
          </main>

          <footer
            class="tw-w-full tw-bg-connx-grey-tint-85 tw-mx-auto tw-border-solid tw-border-t tw-border-connx-grey-tint-80 tw-py-6"
          >
            <div class="tw-px-8">
              <div
                class="tw-flex tw-flex-wrap tw-items-start"
                :class="{
                  'tw-justify-center': currentStep !== 2,
                  'tw-justify-between': currentStep === 2,
                }"
              >
                <connx-field
                  v-model="confirmEmployeeDelete"
                  v-if="currentStep === 2"
                  name="confirm-emp-del"
                  unpadded
                  class="tw-w-full md:tw-w-auto tw-p-0 tw-py-4 md:tw-py-0 md:tw-pr-4 tw-mb-0"
                  :type="InputType.Checkbox"
                >
                  <div>
                    <p class="tw-mb-2">
                      I understand the deletion is permanent and there is no rollback.
                    </p>
                    <p class="tw-mb-0">
                      I still would like to continue with the process.
                    </p>
                  </div>
                </connx-field>

                <div>
                  <connx-button
                    v-if="currentStep > 1 && !completed"
                    icon="fas fa-arrow-left"
                    icon-position="before"
                    aria-label="Previous"
                    class="tw-bg-connx-white tw-w-full md:tw-w-36 md:tw-mr-4 tw-mb-4 md:tw-mb-0"
                    @click="decrementStep()"
                  >
                    Previous
                  </connx-button>
                  <connx-button
                    v-if="!completed"
                    :icon="currentStep === 1 ? 'fas fa-arrow-right' : 'far fa-shield-check'"
                    :icon-position="currentStep === 1 ? 'after' : 'before'"
                    button-style="success"
                    aria-label="Next"
                    :disabled="nextButtonDisabled"
                    @click="incrementStep()"
                  >
                    {{ currentStep === 1 ? 'Next' : 'Complete' }}
                  </connx-button>
                  <connx-button
                    v-if="completed"
                    @click="emit('close')"
                    class="tw-bg-connx-white"
                  >
                    Close
                  </connx-button>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  </div>
</template>
